import type { ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { type CtaSlot, trackEvent } from '../analytics/events.js';
import { signupHref, viaFromPath, viaFromSearch } from '../lib/signupSource.js';

/**
 * A link to `/signup` that carries the channel the reader is standing in.
 *
 * Every public CTA goes through this rather than a bare `<Link to="/signup">`,
 * so attribution is a property of the component instead of something each page
 * has to remember. A comparison page added next month is counted correctly
 * without anyone touching its button.
 *
 * The channel is read from the current location, so this deliberately has no
 * `via` prop: a page cannot claim to be a channel it is not standing on, and
 * there is no second place for the two to disagree.
 *
 * It is also where `cta_click` is sent, for the same reason the `via` lives
 * here: a page that is read and a page that is acted on otherwise look
 * identical in analytics, and putting the call on the shared component means a
 * comparison page added next month is counted without anyone touching its
 * button. `slot` is the one thing the component cannot derive — two of these
 * on a page are two different questions — and it is a closed set, not a label.
 *
 * Nothing is sent from the join screen, where this same button is offered to a
 * voter. That is not handled here: `trackEvent` refuses every audience
 * surface, so it holds however this component is reused.
 */
export function SignupLink({
  className,
  slot,
  children,
}: {
  className?: string;
  slot: CtaSlot;
  children: ReactNode;
}) {
  const location = useLocation();

  return (
    <Link
      to={signupHref(location.pathname, location.search)}
      {...(className ? { className } : {})}
      onClick={() =>
        trackEvent({
          name: 'cta_click',
          // The same channel the href carries, derived the same way, so the
          // click and the signup it becomes cannot be labelled differently.
          via: viaFromSearch(location.search) ?? viaFromPath(location.pathname) ?? 'direct',
          slot,
        })
      }
    >
      {children}
    </Link>
  );
}
